import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { 
  Tooltip as UITooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { InfoIcon } from 'lucide-react';

interface SymptomFrequencyChartProps {
  data: any[];
  selectedSymptoms: string[];
}

export const SymptomFrequencyChart: React.FC<SymptomFrequencyChartProps> = ({ data, selectedSymptoms }) => {
  // Count the frequency of each selected symptom
  const frequencyData = React.useMemo(() => {
    const counts: { [key: string]: number } = {};
    
    // Initialize with zeros
    selectedSymptoms.forEach(symptom => {
      counts[symptom] = 0;
    });
    
    // Count occurrences
    data.forEach(entry => {
      entry.symptoms.forEach((symptom: string) => {
        if (selectedSymptoms.includes(symptom)) {
          counts[symptom] = (counts[symptom] || 0) + 1;
        }
      });
    });
    
    // Transform to chart format
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, [data, selectedSymptoms]);
  
  // Define colors for each symptom
  const symptomColors = {
    'Fatigue': '#FFA07A', // Light Salmon
    'Headache': '#FF6B6B', // Light Red
    'Bloating': '#FFD700', // Gold
    'Cramps': '#FF4500', // Orange Red
    'Mood Swings': '#9370DB', // Medium Purple
    'Insomnia': '#4682B4', // Steel Blue
    'Low Energy': '#7B68EE', // Medium Slate Blue
    'Cravings': '#32CD32', // Lime Green
    // Add more symptoms with their colors as needed
  };
  
  // Configuration for chart
  const chartConfig = {
    ...frequencyData.reduce((acc, { name }) => {
      // @ts-ignore
      acc[name] = { color: symptomColors[name] || '#CCCCCC' };
      return acc;
    }, {})
  };
  
  // Custom tooltip formatter
  const customTooltipFormatter = (value: number, name: string) => {
    return [
      `${value} ${value === 1 ? 'occurrence' : 'occurrences'}`, 
      `Frequency`
    ];
  };
  
  return (
    <div className="w-full h-[400px]">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-hormony-charcoal">Symptom Frequency Analysis</h3>
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help">
                <InfoIcon size={16} className="text-hormony-charcoal/60" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>This chart shows how frequently each symptom was reported during the selected time period.</p>
              <p className="mt-1 text-xs text-hormony-charcoal/70">Longer bars indicate symptoms reported more often.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      
      <ChartContainer config={chartConfig}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={frequencyData}
            margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} />
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="name" 
              width={120}
              tick={{ fontSize: 12 }}
            />
            <ChartTooltip 
              content={<ChartTooltipContent />} 
              formatter={customTooltipFormatter}
            />
            <Legend 
              layout="horizontal" 
              verticalAlign="top" 
              align="center"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar 
              dataKey="count" 
              name="Frequency" 
              background={{ fill: '#f9f9f9' }}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};
